import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';
import { FaRegPaperPlane } from 'react-icons/fa';
import ModalLayout from '../layouts/ModalLayout';
import Button from '../buttons/Button';
import { Connection } from '../../../redux/slices/connectionsSlice';
import localContactService from '../../../services/localContactService';
import { isValidEmail } from '../../../utils/stringUtils';
import { ProfileDataType } from '../../../types/Profile';
import { createEmptyField } from '../../../utils/formatUtils';
import IconBadge from '../icons/IconBadge';

interface ContactEmailData {
  connection: Connection;
  email: string;
  error: string;
}

interface AddEmailsBeforeInviteModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  contactsWithoutEmails: Connection[];
  onEmailsAdded?: () => void;
}

export default function AddEmailsBeforeInviteModal({
  open,
  setOpen,
  contactsWithoutEmails,
  onEmailsAdded,
}: AddEmailsBeforeInviteModalProps): JSX.Element {
  const { t } = useTranslation();
  const [contactEmails, setContactEmails] = useState<ContactEmailData[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (open && contactsWithoutEmails.length > 0) {
      const contactsNeedingEmails = contactsWithoutEmails.filter(
        (connection) => !connection.fields.EMAIL
      );
      
      setContactEmails(
        contactsNeedingEmails.map((connection) => ({
          connection,
          email: '',
          error: '',
        }))
      );

      if (contactsNeedingEmails.length === 0) {
        setOpen(false);
      }
    }
  }, [open, contactsWithoutEmails, setOpen]);

  const validateEmail = (email: string): boolean => {
    if (!email) {
      return false;
    }
    return isValidEmail(email);
  };

  const handleEmailChange = (index: number, email: string): void => {
    setContactEmails((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              email,
              error: email && !validateEmail(email) ? t('invalid.invalid') : '',
            }
          : item
      )
    );
  };

  const validateAllEmails = (): boolean => {
    let allValid = true;
    setContactEmails((prev) =>
      prev.map((item) => {
        const hasError = !item.email || !validateEmail(item.email);
        if (hasError) allValid = false;
        
        return {
          ...item,
          error: !item.email 
            ? t('invalid.empty') 
            : !validateEmail(item.email) 
            ? t('invalid.invalid') 
            : '',
        };
      })
    );
    return allValid;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validateAllEmails()) return;

    setIsSubmitting(true);
    try {
      await Promise.all(
        contactEmails.map(async ({ connection, email }) => {
          const currentContact = await localContactService.fetchLocalContact(connection.id);
          const updatedReceivedData = [...currentContact.receivedData];
          const existingEmailIndex = updatedReceivedData.findIndex(
            (field) => field.dataType === ProfileDataType.EMAIL
          );

          if (existingEmailIndex >= 0) {
            updatedReceivedData[existingEmailIndex] = {
              ...updatedReceivedData[existingEmailIndex],
              email,
            };
          } else {
            const newEmailField = createEmptyField(ProfileDataType.EMAIL);
            newEmailField.email = email;
            updatedReceivedData.push(newEmailField);
          }

          return localContactService.updateLocalContact(
            connection.id,
            currentContact.firstName,
            currentContact.lastName,
            currentContact.note,
            updatedReceivedData,
            currentContact.customFieldResponses
          );
        })
      );

      const contactIds = contactEmails.map((item) => item.connection.id);
      await localContactService.inviteContacts(contactIds);

      toast.success(t('toast.success.inviteSent', { count: contactIds.length }));
      setOpen(false);
      setContactEmails([]);

      if (onEmailsAdded) onEmailsAdded();
    } catch (err) {
      console.error(err);
      toast.error(t('toast.error.unexpected'));
    }

    setIsSubmitting(false);
  };

  const handleClose = (): void => {
    setOpen(false);
    setContactEmails([]);
  };

  const allEmailsValid = contactEmails.every((item) => item.email && validateEmail(item.email));

  return (
    <ModalLayout
      open={open}
      setOpen={handleClose}
      className="z-50 w-full max-w-2xl rounded-[20px] bg-secondary-50 p-6">
      <div className="flex flex-col">
        <div className="mb-6 flex items-center">
          <IconBadge icon={FaRegPaperPlane} />
          <div className="ml-4">
            <h2 className="text-2xl font-semibold text-primary-900">
              {t('component.modal.addEmailsBeforeInvite.title')}
            </h2>
            <p className="text-sm text-gray-600">
              {t('component.modal.addEmailsBeforeInvite.subtitle', { count: contactEmails.length })}
            </p>
          </div>
        </div>

        <div className="mb-6">
          <p className="mb-4 text-sm text-gray-700">
            {t('component.modal.addEmailsBeforeInvite.description')}
          </p>

          <div className="max-h-96 space-y-4 overflow-y-auto">
            {contactEmails.map((item, index) => (
              <div key={item.connection.id} className="rounded-lg border border-gray-200 p-4">
                <div className="mb-3">
                  <h3 className="font-medium text-gray-900">
                    {item.connection.fields.FIRST_NAME} {item.connection.fields.LAST_NAME}
                  </h3>
                </div>
                
                <div className="relative">
                  <label 
                    htmlFor={`email-input-${index}`} 
                    className="mb-2 block text-sm font-medium text-gray-700">
                    {t('dataType.email')} *
                  </label>
                  <input
                    id={`email-input-${index}`}
                    type="email"
                    value={item.email}
                    onChange={(e) => handleEmailChange(index, e.target.value)}
                    placeholder={t('dataType.email')}
                    className={classNames(
                      'w-full rounded-lg border px-3 py-2 text-gray-600 focus:border-primary-300 focus:outline-none focus:ring-1 focus:ring-primary-300',
                      {
                        '!border-danger': item.error,
                      }
                    )}
                    disabled={isSubmitting}
                  />
                  {item.error && (
                    <p className="mt-1 text-sm text-danger">{item.error}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="tertiary"
            onClick={handleClose}
            disabled={isSubmitting}>
            {t('general.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !allEmailsValid || contactEmails.length === 0}>
            {isSubmitting ? t('general.loading') : t('component.modal.addEmailsBeforeInvite.sendInvites')}
          </Button>
        </div>
      </div>
    </ModalLayout>
  );
}
